import { io, type Socket } from "socket.io-client";
import {
  ANALYTICS_CONSENT_POLICY_VERSION,
  AnalyticsAssociationResultSchema,
  CommandAckSchema,
  LobbyViewSchema,
  PlayerViewSchema,
  ResolutionEventsPayloadSchema,
  ServerErrorPayloadSchema,
  SessionEstablishedSchema,
  type ClientOrder,
  type GameMode,
  type ClientToServerEvents,
  type CommandAck,
  type PlayerEvent,
  type ServerToClientEvents,
} from "@pactfront/contracts";

import {
  beginAnalyticsMatchEntry,
  getAnalyticsAssociationContext,
  getAnalyticsConsent,
  recordProductError,
  recordReconnect,
  setAnalyticsMatchContext,
  subscribeAnalyticsConsent,
  type AnalyticsErrorSurface,
} from "./analytics.js";
import { useClientStore } from "./store.js";
import { serverUrl } from "./serverUrl.js";

type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const sessionKey = "pactfront.session.v1";
const cursorKey = "pactfront.deliveryCursor.v1";
let hasConnected = false;
let resumeInProgress = false;
let pendingResolutionEvents: PlayerEvent[] = [];
let pendingResolutionFlush: number | undefined;

function takePendingResolutionEvents(): PlayerEvent[] {
  if (pendingResolutionFlush !== undefined) {
    window.clearTimeout(pendingResolutionFlush);
    pendingResolutionFlush = undefined;
  }
  const pending = pendingResolutionEvents;
  pendingResolutionEvents = [];
  return pending;
}

function flushPendingResolutionEvents(): void {
  const pending = takePendingResolutionEvents();
  if (pending.length > 0) useClientStore.getState().addEvents(pending);
}

export const socket: ClientSocket = io(serverUrl, {
  transports: ["websocket"],
  autoConnect: true,
});

function commandId(): string {
  return crypto.randomUUID();
}

function currentErrorSurface(): AnalyticsErrorSurface {
  const state = useClientStore.getState();
  if (!state.connected) return "reconnect";
  if (state.view !== null) return "match";
  if (state.lobby !== null) return "lobby";
  return "landing";
}

function acknowledged(
  send: (ack: (response: CommandAck) => void) => void,
  recordTimeout = true,
): Promise<CommandAck> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      if (recordTimeout) recordProductError(currentErrorSurface(), "network_timeout");
      resolve({ ok: false, code: "internal", messageKey: "error.network" });
    }, 5_000);
    send((response) => {
      window.clearTimeout(timeout);
      const parsed = CommandAckSchema.safeParse(response);
      resolve(
        parsed.success ? parsed.data : { ok: false, code: "internal", messageKey: "error.network" },
      );
    });
  });
}

function report(response: CommandAck): CommandAck {
  if (!response.ok) {
    recordProductError(currentErrorSurface(), `command_${response.code}`);
    useClientStore.getState().showNotice("error", response.messageKey, response.params);
  }
  return response;
}

function recoverFromInvalidSession(attemptedToken: string, response: CommandAck): void {
  if (response.ok || response.code !== "not_authenticated") {
    void report(response);
    return;
  }

  recordProductError("reconnect", "session_invalid");

  // A newer session may have been established while this resume acknowledgement was in flight.
  // Only clear persisted and in-memory state when the rejected token is still current.
  if (localStorage.getItem(sessionKey) !== attemptedToken) return;
  localStorage.removeItem(sessionKey);
  localStorage.removeItem(cursorKey);
  useClientStore.getState().reset();
}

async function syncServerAnalyticsContext(): Promise<void> {
  const context = getAnalyticsAssociationContext();
  if (context === null || useClientStore.getState().session === null) return;
  const response = await acknowledged(
    (ack) =>
      socket.emit(
        "analytics.associate",
        {
          clientCommandId: commandId(),
          consentPolicyVersion: ANALYTICS_CONSENT_POLICY_VERSION,
          context,
        },
        ack,
      ),
    false,
  );
  if (!response.ok || getAnalyticsConsent() !== "granted") return;
  const result = AnalyticsAssociationResultSchema.safeParse(response.data);
  if (result.success) setAnalyticsMatchContext(result.data.matchContext);
}

function withdrawServerAnalyticsContext(): void {
  setAnalyticsMatchContext(null);
  if (useClientStore.getState().session === null || !socket.connected) return;
  void acknowledged(
    (ack) => socket.emit("analytics.withdraw", { clientCommandId: commandId() }, ack),
    false,
  );
}

subscribeAnalyticsConsent(() => {
  if (getAnalyticsConsent() === "granted") void syncServerAnalyticsContext();
  else withdrawServerAnalyticsContext();
});

socket.on("connect", () => {
  if (hasConnected) recordReconnect();
  hasConnected = true;
  useClientStore.getState().setConnected(true);
  const token = localStorage.getItem(sessionKey);
  if (token !== null) {
    resumeInProgress = true;
    const cursor = Number(localStorage.getItem(cursorKey) ?? "0");
    socket.emit(
      "session.resume",
      { clientCommandId: commandId(), token, deliveryCursor: Number.isFinite(cursor) ? cursor : 0 },
      (response) => recoverFromInvalidSession(token, response),
    );
  }
});
socket.on("disconnect", () => useClientStore.getState().setConnected(false));
socket.on("session.established", (payload) => {
  const session = SessionEstablishedSchema.parse(payload);
  localStorage.setItem(sessionKey, session.token);
  useClientStore.getState().setSession(session);
  void syncServerAnalyticsContext();
});
socket.on("lobby.updated", (payload) => {
  resumeInProgress = false;
  useClientStore.getState().setLobby(LobbyViewSchema.parse(payload));
});
socket.on("match.view", (payload) => {
  const view = PlayerViewSchema.parse(payload);
  const previousMatchId = useClientStore.getState().view?.matchId;
  if (previousMatchId !== view.matchId) {
    beginAnalyticsMatchEntry(resumeInProgress ? "resume" : "start");
  }
  resumeInProgress = false;
  useClientStore.getState().setView(view, takePendingResolutionEvents());
});
socket.on("resolution.events", (payload) => {
  const resolution = ResolutionEventsPayloadSchema.parse(payload);
  localStorage.setItem(cursorKey, String(resolution.toCursor));
  pendingResolutionEvents.push(...resolution.events);
  if (pendingResolutionFlush !== undefined) window.clearTimeout(pendingResolutionFlush);
  // Normal resolution delivery is events followed by the authoritative final view. Holding this
  // batch briefly lets the view handler publish both atomically; reconnect delivery where the view
  // already arrived uses this bounded fallback.
  pendingResolutionFlush = window.setTimeout(flushPendingResolutionEvents, 50);
});
socket.on("error", (payload) => {
  const error = ServerErrorPayloadSchema.parse(payload);
  recordProductError(currentErrorSurface(), "realtime_error");
  useClientStore.getState().showNotice("error", error.messageKey);
});

export function returnToLanding(): void {
  takePendingResolutionEvents();
  setAnalyticsMatchContext(null);
  socket.disconnect();
  localStorage.removeItem(sessionKey);
  localStorage.removeItem(cursorKey);
  useClientStore.getState().reset();
  window.setTimeout(() => socket.connect(), 0);
}

export const commands = {
  async createLobby(name: string) {
    const session = report(
      await acknowledged((ack) =>
        socket.emit("session.create", { clientCommandId: commandId(), name }, ack),
      ),
    );
    if (!session.ok) return session;
    await syncServerAnalyticsContext();
    return report(
      await acknowledged((ack) =>
        socket.emit("lobby.create", { clientCommandId: commandId() }, ack),
      ),
    );
  },
  async joinLobby(name: string, code: string) {
    const session = report(
      await acknowledged((ack) =>
        socket.emit("session.create", { clientCommandId: commandId(), name }, ack),
      ),
    );
    if (!session.ok) return session;
    await syncServerAnalyticsContext();
    return report(
      await acknowledged((ack) =>
        socket.emit("lobby.join", { clientCommandId: commandId(), code: code.toUpperCase() }, ack),
      ),
    );
  },
  selectCountry(code: string, countryId: string) {
    return acknowledged((ack) =>
      socket.emit("lobby.selectCountry", { clientCommandId: commandId(), code, countryId }, ack),
    ).then(report);
  },
  setLobbyReady(code: string, ready: boolean) {
    return acknowledged((ack) =>
      socket.emit("lobby.setReady", { clientCommandId: commandId(), code, ready }, ack),
    ).then(report);
  },
  setLobbyMode(code: string, gameMode: GameMode) {
    return acknowledged((ack) =>
      socket.emit("lobby.setMode", { clientCommandId: commandId(), code, gameMode }, ack),
    ).then(report);
  },
  rerollComputerCountries(code: string) {
    return acknowledged((ack) =>
      socket.emit("lobby.rerollComputers", { clientCommandId: commandId(), code }, ack),
    ).then(report);
  },
  async startLobby(code: string) {
    const response = report(
      await acknowledged((ack) =>
        socket.emit("lobby.start", { clientCommandId: commandId(), code }, ack),
      ),
    );
    if (response.ok) await syncServerAnalyticsContext();
    return response;
  },
  upsertOrder(order: ClientOrder) {
    return acknowledged((ack) =>
      socket.emit("orders.upsert", { clientCommandId: commandId(), order }, ack),
    ).then(report);
  },
  removeOrder(matchId: string, turnNumber: number, replacementKey: string) {
    return acknowledged((ack) =>
      socket.emit(
        "orders.remove",
        { clientCommandId: commandId(), matchId, turnNumber, replacementKey },
        ack,
      ),
    ).then(report);
  },
  setOrdersReady(matchId: string, turnNumber: number, ready: boolean) {
    return acknowledged((ack) =>
      socket.emit(
        "orders.setReady",
        { clientCommandId: commandId(), matchId, turnNumber, ready },
        ack,
      ),
    ).then(report);
  },
  proposeAgreement(
    matchId: string,
    targetCountryId: string,
    agreement: "nap" | "trade" | "alliance",
  ) {
    return acknowledged((ack) =>
      socket.emit(
        "diplomacy.propose",
        { clientCommandId: commandId(), matchId, targetCountryId, agreement },
        ack,
      ),
    ).then(report);
  },
  respondToAgreement(matchId: string, proposalId: string, accept: boolean) {
    return acknowledged((ack) =>
      socket.emit(
        "diplomacy.respond",
        { clientCommandId: commandId(), matchId, proposalId, accept },
        ack,
      ),
    ).then(report);
  },
  declareWar(matchId: string, targetCountryId: string, confirmBetrayal: boolean) {
    return acknowledged((ack) =>
      socket.emit(
        "diplomacy.declareWar",
        { clientCommandId: commandId(), matchId, targetCountryId, confirmBetrayal },
        ack,
      ),
    ).then(report);
  },
  breakAgreements(
    matchId: string,
    targetCountryId: string,
    agreements: Array<"nap" | "trade" | "alliance">,
  ) {
    return acknowledged((ack) =>
      socket.emit(
        "diplomacy.break",
        {
          clientCommandId: commandId(),
          matchId,
          targetCountryId,
          agreements,
          confirmBetrayal: true,
        },
        ack,
      ),
    ).then(report);
  },
  selectPolitics(matchId: string, cardId: string | null, targetCityId: string | null) {
    return acknowledged((ack) =>
      socket.emit(
        "politics.select",
        { clientCommandId: commandId(), matchId, cardId, targetCityId },
        ack,
      ),
    ).then(report);
  },
};
